import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Sheet } from '../../design/Sheet'
import { GymMark } from './GymMark'
import type { MyGym } from '../gym/useMyGyms'

interface Props {
  isOpen: boolean
  onClose: () => void
  gyms: MyGym[]
  activeSlug: string | null
  /**
   * A child's own login: it switches between the gyms where the child has a plan and has no gym
   * list to join from, and no dashboard to land on.
   */
  childAccount?: boolean
}

/**
 * The list of gyms a member belongs to, opened from their logo in the header.
 *
 * This is what makes four tabs enough: the gym is a persistent piece of context
 * at the top of the app rather than a ninth entry in a menu, so switching does
 * not cost a destination.
 */
export function GymSwitcherSheet({
  isOpen,
  onClose,
  gyms,
  activeSlug,
  childAccount = false,
}: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const go = (to: string) => {
    onClose()
    void navigate(to)
  }

  return (
    <Sheet isOpen={isOpen} onClose={onClose} aria-label={t('nav.switchGym')}>
      <div className="px-5 pb-6 pt-2">
        <h2 className="eyebrow mb-3 text-foreground/40">{t('nav.yourGyms')}</h2>
        <ul className="flex flex-col gap-1">
          {gyms.map((gym) => {
            const isActive = gym.slug === activeSlug
            return (
              <li key={gym.id}>
                <button
                  onClick={() =>
                    go(childAccount ? `/${gym.slug}/bouldering/routes` : `/${gym.slug}/dashboard`)
                  }
                  aria-current={isActive ? 'true' : undefined}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors ${
                    isActive ? 'bg-[var(--gym-veil)]' : 'hover:bg-[var(--gym-veil)]/60'
                  }`}
                >
                  <GymMark name={gym.name} imageUrl={gym.image_url} size="md" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{gym.name}</span>
                  {isActive && (
                    <span aria-hidden className="text-sm text-[var(--gym-primary)]">
                      ✓
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>

        {!childAccount && (
          <button
            onClick={() => go('/our-gyms')}
            className="mt-3 w-full rounded-md border border-dashed border-[var(--gym-line)] px-3 py-2.5 text-sm font-medium text-foreground/60 transition-colors hover:text-foreground"
          >
            {t('ourGyms.menuLink')}
          </button>
        )}
      </div>
    </Sheet>
  )
}
